import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '../utils/cn';

/**
 * Drawer Component - Feedback
 * Slide-in panel from right side
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Drawer open state
 * @param {Function} props.onClose - Close handler
 * @param {string} props.title - Drawer title
 * @param {React.ReactNode} props.children - Drawer content
 * @param {string} props.size - 'sm' | 'md' | 'lg' | 'xl'
 * @param {string} props.position - 'left' | 'right' | 'top' | 'bottom'
 */
const Drawer = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  position = 'right',
  className = '',
  ...props
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizes = {
    sm: 'w-full sm:w-80',
    md: 'w-full sm:w-96',
    lg: 'w-full sm:w-[28rem]',
    xl: 'w-full sm:w-[32rem]',
  };

  const positions = {
    right: 'right-0 top-0 h-full',
    left: 'left-0 top-0 h-full',
    top: 'top-0 left-0 w-full',
    bottom: 'bottom-0 left-0 w-full',
  };

  const slideAnimations = {
    right: 'translate-x-0',
    left: 'translate-x-0',
    top: 'translate-y-0',
    bottom: 'translate-y-0',
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'drawer-title' : undefined}
    >
      {/* Backdrop */}
      <div
        className={cn(
          'absolute inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm transition-opacity',
          isOpen ? 'opacity-100' : 'opacity-0'
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer Content */}
      <div
        className={cn(
          'absolute bg-white dark:bg-primary-800 flex flex-col transition-transform duration-300 ease-in-out',
          'shadow-[0_25px_50px_-12px_rgba(0,0,0,0.12)] dark:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.7)]',
          positions[position],
          sizes[size],
          slideAnimations[position],
          className
        )}
        {...props}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between p-6 border-b border-primary-200 dark:border-primary-700 flex-shrink-0">
            <h2
              id="drawer-title"
              className="text-xl font-semibold text-primary-900 dark:text-primary-50"
            >
              {title}
            </h2>
            <button
              onClick={onClose}
              className="text-primary-400 hover:text-primary-600 dark:hover:text-primary-300 transition-colors p-1 rounded hover:bg-primary-100 dark:hover:bg-primary-700"
              aria-label="Close drawer"
            >
              <X size={24} />
            </button>
          </div>
        )}

        {/* Body */}
        <div className={cn('flex-1 overflow-y-auto', title ? 'p-6' : 'p-6 pt-6')}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Drawer;
